import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export type ThemeType = 'light' | 'dark' | 'glass' | 'neumorph' | 'cyber';

interface ThemeContextType {
  currentTheme: ThemeType;
  setTheme: (theme: ThemeType) => void;
  isAutoMode: boolean;
  setAutoMode: (auto: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  currentTheme: 'light',
  setTheme: () => {},
  isAutoMode: true,
  setAutoMode: () => {},
});

export const useTheme = () => useContext(ThemeContext);

interface ThemeAdaptorProviderProps {
  children: React.ReactNode;
}

export const ThemeAdaptorProvider: React.FC<ThemeAdaptorProviderProps> = ({ children }) => {
  const location = useLocation();
  // 默认启用自动模式，除非用户手动切换过
  const [isAutoMode, setIsAutoMode] = useState<boolean>(() => {
    return localStorage.getItem('theme_auto_mode') !== 'false';
  });
  
  const [currentTheme, setCurrentThemeState] = useState<ThemeType>(() => {
    const savedTheme = localStorage.getItem('theme');
    // 如果有保存的主题且不是自动模式，则使用保存的主题
    if (savedTheme && localStorage.getItem('theme_auto_mode') === 'false') {
      return savedTheme as ThemeType;
    }
    return 'light'; // 默认使用简约白主题
  });

  // 设置主题的内部函数
  const applyTheme = (theme: ThemeType) => {
    const root = document.documentElement;
    // 移除所有可能的主题属性
    root.removeAttribute('data-theme');
    
    // 如果不是light（默认），则设置data-theme
    if (theme !== 'light') {
      root.setAttribute('data-theme', theme);
    }
    
    // 同时为了兼容旧代码，如果包含dark/tech，可能需要设置class或其他
    // 这里主要依赖 data-theme 属性
  };

  // 用户手动切换主题
  const setTheme = (theme: ThemeType) => {
    setIsAutoMode(false);
    localStorage.setItem('theme_auto_mode', 'false');
    localStorage.setItem('theme', theme);
    setCurrentThemeState(theme);
    applyTheme(theme);
  };

  // 恢复自动模式
  const setAutoMode = (auto: boolean) => {
    setIsAutoMode(auto);
    localStorage.setItem('theme_auto_mode', String(auto));
    if (auto) {
      // 立即触发一次自动适配
      adaptThemeToRoute(location.pathname);
    }
  };

  // 根据路由适配主题
  const adaptThemeToRoute = (pathname: string) => {
    let targetTheme: ThemeType = 'light'; // 默认使用简约白

    if (pathname.startsWith('/login') || pathname === '/') {
      targetTheme = 'light'; // 登录页/首页 -> 简约白
    } else if (pathname.startsWith('/dashboard') || pathname.startsWith('/reports')) {
      targetTheme = 'light'; // 仪表盘/报表 -> 简约白
    } else if (pathname.startsWith('/system') || pathname.startsWith('/price-config') || pathname.startsWith('/settings')) {
      targetTheme = 'light'; // 系统设置/价格配置 -> 简约白
    } else if (pathname.startsWith('/project') || pathname.startsWith('/forms') || pathname.startsWith('/data')) {
      targetTheme = 'light'; // 管理/表单 -> 简约白
    } else {
      // 其他页面默认使用简约白
      targetTheme = 'light'; 
    }

    setCurrentThemeState(targetTheme);
    applyTheme(targetTheme);
  };

  // 监听路由变化
  useEffect(() => {
    if (isAutoMode) {
      adaptThemeToRoute(location.pathname);
    }
  }, [location.pathname, isAutoMode]);

  // 初始化应用主题
  useEffect(() => {
    applyTheme(currentTheme);
  }, []);

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, isAutoMode, setAutoMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
